import { JobsService } from '@/api/generated/services/JobsService'
import type { JobResponse } from '@/api/generated/models/JobResponse'

/**
 * AI 분석 작업(Job) 공용 유틸.
 *
 * 백엔드 AI 분석이 동기 호출에서 비동기 Job 방식으로 전환됨에 따라,
 * 등록(POST /api/jobs/ai-analysis) 후 종료 상태가 될 때까지
 * GET /api/jobs/{id} 를 폴링하는 흐름을 여러 화면에서 공유한다.
 */

export const JOB_TERMINAL_STATUSES = [
  'completed',
  'completed_with_errors',
  'failed',
  'canceled',
] as const

export const isJobTerminal = (status: string): boolean =>
  (JOB_TERMINAL_STATUSES as readonly string[]).includes(status)

export const isJobActive = (status: string): boolean => !isJobTerminal(status)

/** 단건 job 상태 폴링 간격 */
export const JOB_POLL_INTERVAL_MS = 3000
/** 작업 목록(배너) 폴링 간격 */
export const JOB_LIST_POLL_INTERVAL_MS = 5000

export const formatJobStatus = (status: string): string => {
  switch (status) {
    case 'pending':
      return '대기 중'
    case 'running':
      return '진행 중'
    case 'completed':
      return '완료'
    case 'completed_with_errors':
      return '일부 실패'
    case 'failed':
      return '실패'
    case 'canceled':
      return '취소됨'
    default:
      return status
  }
}

/**
 * job이 종료 상태(completed/completed_with_errors/failed/canceled)가 될 때까지 폴링.
 * isCancelled()가 true를 반환하면 조용히 중단하고 null을 반환한다
 * (패널 닫힘/언마운트 시 폴링 중단용 — 서버 측 분석은 계속 진행됨).
 */
export const pollJobUntilDone = async (
  jobId: string,
  options: {
    intervalMs?: number
    isCancelled?: () => boolean
    onProgress?: (job: JobResponse) => void
  } = {}
): Promise<JobResponse | null> => {
  const { intervalMs = JOB_POLL_INTERVAL_MS, isCancelled, onProgress } = options

  for (;;) {
    if (isCancelled?.()) return null
    const job = await JobsService.appsJobsViewsGetJob({ jobId })
    if (isCancelled?.()) return null

    onProgress?.(job)
    if (isJobTerminal(job.status)) return job

    await new Promise(resolve => setTimeout(resolve, intervalMs))
    if (isCancelled?.()) return null
  }
}

export interface FailedJobItemMessage {
  /** report 스코프 항목이면 payload.field_key (템플릿 필드 키), 없으면 null */
  fieldKey: string | null
  message: string
}

/**
 * 실패 항목들의 error_message 수집 — completed_with_errors/failed 안내용.
 */
export const fetchFailedItemMessages = async (
  jobId: string,
  maxItems = 20
): Promise<FailedJobItemMessage[]> => {
  const res = await JobsService.appsJobsViewsListJobItems({
    jobId,
    status: 'failed',
    perPage: maxItems,
  })
  return res.data.map(item => ({
    fieldKey:
      typeof item.payload?.field_key === 'string' ? item.payload.field_key : null,
    message: item.error_message?.trim() || '알 수 없는 오류',
  }))
}

/**
 * 진행 중인 AI 분석 작업 기억 (sessionStorage).
 *
 * 분석은 서버 백그라운드에서 진행되므로, 사이드패널을 닫았다 다시 열거나
 * 새로고침해도 같은 대상의 작업을 이어서 추적할 수 있어야 한다
 * (진행 표시 유지 + 분석 버튼 중복 클릭 방지).
 * 작업이 종료 상태가 되면 반드시 forgetActiveJob으로 정리한다.
 */
const ACTIVE_JOB_STORAGE_KEY = 'dot_active_ai_jobs'

/** 보고서 단위 추적 키 */
export const reportJobKey = (reportId: string): string => `report:${reportId}`

const readActiveJobs = (): Record<string, string> => {
  try {
    const raw = sessionStorage.getItem(ACTIVE_JOB_STORAGE_KEY)
    const parsed = raw ? JSON.parse(raw) : null
    return parsed && typeof parsed === 'object' ? (parsed as Record<string, string>) : {}
  } catch {
    return {}
  }
}

const writeActiveJobs = (map: Record<string, string>): void => {
  try {
    sessionStorage.setItem(ACTIVE_JOB_STORAGE_KEY, JSON.stringify(map))
  } catch {
    // 저장 실패(프라이빗 모드 등)는 무시 — 재진입 시 추적만 못 할 뿐 분석은 정상 진행된다
  }
}

export const rememberActiveJob = (key: string, jobId: string): void => {
  const map = readActiveJobs()
  map[key] = jobId
  writeActiveJobs(map)
}

export const forgetActiveJob = (key: string): void => {
  const map = readActiveJobs()
  if (!(key in map)) return
  delete map[key]
  writeActiveJobs(map)
}

export const getActiveJobId = (key: string): string | null => readActiveJobs()[key] ?? null
